import { X } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { createPortal } from "preact/compat";
import { useEffect, useState } from "preact/hooks";

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  children: ComponentChildren;
  class?: string;
  controlledClose?: boolean;
  /** Replaces the default `p-6` padding of the scrolling content. */
  contentClass?: string;
  testId?: string;
  hideCloseButton?: boolean;
};

const CLOSE_BUTTON_CLASS = "absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors z-10";

/** Rendered into `document.body`, and only after mount, so the prerender and hydration never see it. */
export const Modal = ({
  isOpen,
  onClose,
  children,
  class: className = "",
  controlledClose = false,
  contentClass = "p-6",
  testId,
  hideCloseButton,
}: ModalProps) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isOpen || !isMounted) {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      data-testid={testId}
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-5000 p-4"
      onClick={controlledClose ? undefined : onClose}
    >
      <div
        class={`bg-white dark:bg-neutral-800 rounded-lg w-full max-w-md relative max-h-[90vh] flex flex-col ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!controlledClose && !hideCloseButton && (
          <button type="button" onClick={onClose} class={CLOSE_BUTTON_CLASS} aria-label="Close">
            <X size={20} />
          </button>
        )}
        <div class={`overflow-y-auto ${contentClass}`}>{children}</div>
      </div>
    </div>,
    document.body,
  );
};
